/* eslint-disable prettier/prettier */
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { Formik, FormikValues, Form } from 'formik';
import { RootState } from 'store';
import * as yup from 'yup';

import { setUsersLoyalties } from 'action-creators/userLoyalty';
import BottomSheet from 'components/BottomSheet';
import Button from 'components/Buttons/Button';
import SimpleAlert from 'components/SimpleAlert';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useUserAccount from 'hooks/useUserAccount';
import { CostCenter } from 'models/costCenter';
import { ProductType } from 'models/enums/productType';
import { FlightValidationResponse } from 'models/flightValidation';
import { Price } from 'models/price';
import { UserLoyalty } from 'reducers/userLoyalty';
import ConfirmProfile from 'screens/components/Booking/ConfirmProfile';
import TravelJustify from 'screens/components/TravelJustify';
import { getCostCenters } from 'services/costCenterService';
import { Alliance, fetchGetAllAlliances, getAllLoyaltyByUser, Loyalty } from 'services/loyaltiesService';
import { BreakRulesReason } from 'services/travelBreakRulesReasonService';
import { Reason } from 'services/travelReasonService';

import {
  Checkout,
  ConfirmButton,
  ChurnContainer,
  SupportAvatar,
  ChurnTitle,
  ChurnDetail,
  CancelButton,
} from './styles';
import { PaymentCard } from 'models/paymentCard';
import useCompanyConfiguration from 'hooks/useCompanyConfiguration';

interface FlightForm {
  costCenter?: string;
  justifyForBreakingRules: string;
  reason?: Reason;
  breakRulesReason?: BreakRulesReason;
  card?:PaymentCard
}

interface Props {
  loading: boolean;
  price: Price;
  isPreApproved: boolean;
  isBestOption: boolean;
  onConfirm: (
    justifyForBreakingRules: string,
    reason: string,
    costCenterId?: string,
    breakRulesReasonId?: string,
    cardId?:string
  ) => void;
  onClose: () => void;
  showChurning: boolean;
  setShowChurning: (show: boolean) => void;
  details?: FlightValidationResponse;
  alternativePrice?: Price;
  isBySector?: boolean
}

const DetailsCheckout = ({
  loading,
  price,
  onConfirm,
  onClose,
  showChurning,
  setShowChurning,
  details,
  alternativePrice,
  isBySector
}: Props) => {
  const { t } = useStoreTranslation();
  const [showConfirmProfile, setShowConfirmProfile] = useState(false);
  const [showJustify, setShowJustify] = useState(false);
  const userAccount = useUserAccount();
  const [creditCardByUser, setCreditCardByUser] = useState(Boolean(userAccount.creditCardByUser));
  const { travelers, departurePoint, arrivalPoint } = useSelector((state: RootState) => state.flights);
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [loyaltyList, setLoyaltyList] = useState<Loyalty[] | undefined>(undefined);
  const [allianceList, setAllianceList] = useState<Alliance[] | undefined>(undefined);
  const isInternational = useMemo(
    () => departurePoint?.country !== arrivalPoint?.country,
    [departurePoint, arrivalPoint],
  );
  const history = useHistory();
  const search = useSelector((state: RootState) => state.flights);
  const dispatch = useDispatch();
  const companyConfiguration = useCompanyConfiguration();
  const paymentExceptionProvider = useMemo(
    () => details?.tripOption?.provider || details?.airValidateResponse?.[0]?.tripOption?.provider,
    [details],
  );
  const paymentExceptionAirlines = useMemo(
    () =>
      (details?.tripOption ? [details.tripOption] : details?.airValidateResponse?.map((item) => item.tripOption) || [])
        .flatMap((tripOption) => tripOption?.sectors || [])
        .flatMap((sector) => sector?.flights || [])
        .map((flight) => flight?.airlineCode)
        .filter((airlineCode): airlineCode is string => Boolean(airlineCode)),
    [details],
  );
  
  const initialValues: FlightForm = useMemo(
    () =>
      costCenters && costCenters.length > 0
        ? { justifyForBreakingRules: '', reason: undefined, costCenter: '', breakRulesReason: undefined }
        : {
            justifyForBreakingRules: '',
            reason: undefined,
            breakRulesReason: undefined,
          },
    [costCenters],
  );

  useEffect(() => {
    getCostCenters(userAccount.companyId).then((res) => {
      if (res && res.data && res.data.totalResults > 0) {
        const centers = res.data.result.filter((c) => c.isActive);
        if (centers.length > 0) setCostCenters(centers);
      }
    });
  }, [t]);

  const fetchAlliancesAndLoyalties = async () => {
    const alliances = await fetchGetAllAlliances();
    setAllianceList(alliances);

    let loyalties = [];
    let response;

    //TODO método para tener todos los loyalties de una
    if (travelers && travelers.length > 0) {
      for (let index = 0; index < travelers.length; index++) {
        const traveler = travelers[index];
        response = await getAllLoyaltyByUser(traveler.id);
        loyalties = loyalties.concat(response);
      }
    } else {
      response = await getAllLoyaltyByUser(userAccount.id);
      loyalties = loyalties.concat(response);
    }

    setLoyaltyList(loyalties);
  };

  useEffect(() => {
    fetchAlliancesAndLoyalties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateUserLoyalty = () => {
    if (allianceList !== undefined && loyaltyList !== undefined && !isBySector) {
      const airlineCode = details?.tripOption.sectors.first().flights.first().airlineCode ?? '';
      const airlineName = details?.tripOption.sectors.first().flights.first().airlineName ?? '';
      const loyalties = loyaltyList?.filter((value) => value.loyaltyId === airlineCode);

      if (loyalties && loyalties.length > 0) {
        dispatch(
          setUsersLoyalties(
            travelers.map(
              (t) =>
                ({
                  ...t,
                  loyaltyNumber: loyalties.find((l) => l.userId === t.userId || l.userId === t.id)?.loyaltyNumber ?? '',
                  loyaltyName:
                    loyalties.find((l) => l.userId === t.userId || l.userId === t.id)?.loyaltyName ?? airlineName ?? '',
                  airlineCode: airlineCode ?? '',
                } as UserLoyalty),
            ),
          ),
        );
      } else {
        const newLoyalty = allianceList?.filter((value) => value.id === airlineCode).first();
        if (newLoyalty) {
          dispatch(
            setUsersLoyalties(
              travelers.map(
                (t) =>
                  ({
                    ...t,
                    loyaltyNumber: '',
                    loyaltyName: newLoyalty.name ?? airlineName ?? '',
                    airlineCode: airlineCode ?? '',
                  } as UserLoyalty),
              ),
            ),
          );
        } else {
          dispatch(
            setUsersLoyalties(
              travelers.map(
                (t) =>
                  ({
                    ...t,
                    loyaltyNumber: '',
                    loyaltyName: airlineName ?? '',
                    airlineCode: airlineCode ?? '',
                  } as UserLoyalty),
              ),
            ),
          );
        }
      }
    }
  };

  useEffect(() => {
    updateUserLoyalty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allianceList, loyaltyList]);

  const handleSubmit = ({ justifyForBreakingRules, reason, costCenter, breakRulesReason, card }: FlightForm) => {
    if (reason) onConfirm(justifyForBreakingRules, reason.id, costCenter, breakRulesReason?.id,card?.cardId);
  };
  const isCreditCard = () => {
    if(creditCardByUser)
      return yup.object().required();
    else
      return yup.object().notRequired();
  }
  const validationSchema = useMemo(
    () =>
      costCenters && costCenters.length > 0
        ? yup.object().shape({
            justifyForBreakingRules: companyConfiguration?.menuView?.hideTravelReason ? yup.string().min(10).max(300).trim() : yup.string().min(10).max(300).trim().required(),
            card: isCreditCard(),
            reason: yup.object().required(),
            costCenter: yup.string().required(),
          })
        : yup.object().shape({
            justifyForBreakingRules: companyConfiguration?.menuView?.hideTravelReason ? yup.string().min(10).max(300).trim() : yup.string().min(10).max(300).trim().required(),
            reason: yup.object().required(),
            card: isCreditCard(),
          }),
    [costCenters, creditCardByUser],
  );


  if (!userAccount) return null;

  return (
    <>
      <Checkout>
        {userAccount?.bookingAllowed ? (
          <>
            <ConfirmButton
              onClick={() => {
                setShowConfirmProfile(true);
              }}
              testId="flight-details-select-button"
            >
              {t('flightDetails.selectButton')}
            </ConfirmButton>
          </>
        ) : (
          <SimpleAlert title={t('bookingNotAllowedAlert.title')} content={t('bookingNotAllowedAlert.content')} />
        )}
      </Checkout>

      <ConfirmProfile
        show={showConfirmProfile}
        productType={ProductType.Flight}
        onClose={() => setShowConfirmProfile(false)}
        onConfirm={() => setShowJustify(true)}
        user={
          travelers && travelers.length > 0
            ? { ...travelers[0], nationality: travelers[0].country, userId: travelers[0].id || travelers[0].userId }
            : { userId: userAccount.id, ...userAccount }
        }
        isInternationalFlight={isInternational}
        onSaveLoyaltyProgram={fetchAlliancesAndLoyalties}
      />

      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
        isInitialValid={(formik: FormikValues) => validationSchema.isValidSync(formik.initialValues)}
      >
        {({ isValid, handleSubmit: submit }: FormikValues) => (
          <Form>
            <TravelJustify
              show={showJustify}
              onClose={() => setShowJustify(false)}
              isValid={isValid}
              loading={loading}
              itineraryValue={price.total}
              currencyCode={price.currencyCode}
              onSubmit={submit}
              productType={ProductType.Flight}
              costCenters={costCenters}
              breakRulesMessage={details?.breakRulesMessage}
              alternativePrice={alternativePrice?.total}
              alternativePriceCurrencyCode={alternativePrice?.currencyCode}              
              travelerId={(travelers ? travelers[0].id || travelers[0].userId : userAccount.id)}
              paymentExceptionProvider={paymentExceptionProvider}
              paymentExceptionAirlines={paymentExceptionAirlines}
              onCreditCardByUserChange={setCreditCardByUser}
            />
          </Form>
        )}
      </Formik>

      <BottomSheet show={showChurning} modal noHeader onClose={() => {}}>
        <ChurnContainer>
          <SupportAvatar src="/corporate/images/support-avatar.png" />
          <ChurnTitle>{t('flightDetails.churn.title')}</ChurnTitle>
          <ChurnDetail>{t('flightDetails.churn.detail')}</ChurnDetail>

          <Button onClick={() => history.replace('/travelRequested')}>
            {t('flightDetails.churn.viewReservations')}
          </Button>
          <CancelButton
            onClick={() => {
              setShowChurning(false);
              onClose();
            }}
          >
            {t('flightDetails.churn.selectAnotherOption')}
          </CancelButton>
        </ChurnContainer>
      </BottomSheet>
    </>
  );
};

export default DetailsCheckout;
